import { useState, useEffect } from 'react';
import { Card, Badge } from '@weretradeit/ui-react';

/**
 * Aus @weretradeit/ui-react 0.6.4 hierher geholt.
 *
 * Das Bauteil hat in einem Bauteilpaket nichts verloren: es kennt die
 * Modal.com-Konten dieses Hauses beim Namen, spricht einen Endpunkt an, den
 * NUR serviceAnt ausliefert (`workers/modal-service` bedient
 * `/modal/api/jobs`), und kein anderer Verbraucher hat es je gerendert. In
 * ui-react 3.x ist es folgerichtig nicht mehr enthalten.
 *
 * Woertlich uebernommen bis auf die Token: die alte Fassung malte auf
 * `--wt-text-secondary`, `--wt-bg-secondary` und `--wt-border`. Keines der
 * drei definiert v3, auch nicht ueber `compat/v2-aliases.css` — sie waeren
 * ins Leere gelaufen, und zwar lautlos: eine unaufgeloeste
 * Benutzereigenschaft wirft keinen Fehler, die Schrift erbt einfach die
 * Elternfarbe und der Fortschrittsbalken bleibt durchsichtig. Ersetzt durch
 * die v3-Namen `--text-secondary`, `--bg-elevated` und `--border`.
 */

export interface ModalJob {
  appId: string;
  accountKey: 'company' | 'main';
  type: string;
  description: string;
  status: string;
  progress: number;
  currentStep: number;
  totalSteps: number;
  eta: string;
  costSoFar: string;
}

interface AccountJobs {
  account: string;
  accountKey: string;
  jobs: ModalJob[];
  hasActiveJobs: boolean;
  totalProgress: number;
}

interface UseModalJobsOptions {
  refreshInterval?: number;
  apiEndpoint?: string;
}

function useModalJobs({ refreshInterval = 10000, apiEndpoint = '/modal/api/jobs' }: UseModalJobsOptions = {}) {
  const [accountJobs, setAccountJobs] = useState<AccountJobs[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    let cancelled = false;

    const fetchJobs = async () => {
      setLoading(true);
      setError(undefined);
      try {
        const response = await fetch(apiEndpoint, { credentials: 'include' });
        if (!response.ok) throw new Error(`Request failed with ${response.status}`);
        const data: { jobs: ModalJob[] } = await response.json();
        if (cancelled) return;

        const build = (key: 'company' | 'main', label: string): AccountJobs => {
          const jobs = data.jobs.filter((j) => j.accountKey === key);
          return {
            account: label,
            accountKey: key,
            jobs,
            hasActiveJobs: jobs.some((j) => j.status === 'running'),
            totalProgress: jobs.length > 0
              ? Math.round(jobs.reduce((sum, j) => sum + j.progress, 0) / jobs.length)
              : 0,
          };
        };

        setAccountJobs([
          build('company', 'Company (weretradeit)'),
          build('main', 'Main (fscheugenpflug4)'),
        ]);
      } catch (err) {
        /* Der Abbruch beim Ausbau darf nicht als Fehler in der Karte landen:
           die alte Fassung hatte kein `cancelled`, schrieb nach dem Ausbau
           weiter in den Zustand und liess React warnen. */
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchJobs();
    const interval = setInterval(fetchJobs, refreshInterval);
    return () => { cancelled = true; clearInterval(interval); };
  }, [refreshInterval, apiEndpoint]);

  return { accountJobs, loading, error };
}

export interface ModalJobsWidgetProps {
  /** Eigener Titel (Vorgabe: "Modal AI Jobs") */
  title?: string;
  /** Abfrageabstand in Millisekunden (Vorgabe: 10000) */
  refreshInterval?: number;
  /** Kurzfassung ohne Beschreibungen (Vorgabe: false) */
  compact?: boolean;
}

const typeColour = (type: string) =>
  type === 'FLUX' ? '#06d6a0' : type === 'Qwen' ? '#118ab2' : '#8b8b8b';

export function ModalJobsWidget({
  title = 'Modal AI Jobs',
  refreshInterval = 10000,
  compact = false,
}: ModalJobsWidgetProps) {
  const { accountJobs, loading, error } = useModalJobs({ refreshInterval });
  const empty = accountJobs.every((a) => a.jobs.length === 0);

  return (
    <Card title={title} className="modal-jobs-widget">
      {!compact && (
        <p style={{ marginTop: 0, marginBottom: 16, fontSize: '0.9em', color: 'var(--text-secondary)' }}>
          Real-time FLUX and Qwen generation progress across both Modal accounts.
        </p>
      )}

      {loading && <p>Loading Modal jobs…</p>}
      {error && <Badge tone="warn">Failed to load: {error}</Badge>}
      {!loading && !error && empty && (
        <p style={{ color: 'var(--text-secondary)' }}>No active jobs.</p>
      )}

      {!loading && !error && accountJobs.map((account) => (
        <div key={account.accountKey} className="account-section" style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <strong style={{ fontSize: '0.95em' }}>{account.account}</strong>
            {account.hasActiveJobs && <Badge tone="success">Active</Badge>}
            {account.jobs.length > 0 && !account.hasActiveJobs && <Badge tone="info">Idle</Badge>}
          </div>

          {account.jobs.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div className="progress-bar-container">
                <div
                  className="progress-bar-fill"
                  style={{
                    width: `${account.totalProgress}%`,
                    background: account.hasActiveJobs
                      ? 'linear-gradient(90deg, #06d6a0 0%, #118ab2 100%)'
                      : '#8b8b8b',
                  }}
                />
              </div>
              <div style={{ fontSize: '0.85em', color: 'var(--text-secondary)', marginTop: 4 }}>
                {account.totalProgress}% – {account.jobs.length} job{account.jobs.length !== 1 ? 's' : ''}
              </div>
            </div>
          )}

          {account.jobs.map((job) => (
            <div
              key={job.appId}
              className="job-detail"
              style={{
                marginLeft: compact ? 0 : 12,
                paddingLeft: compact ? 0 : 12,
                borderLeft: compact ? 'none' : '2px solid var(--border)',
                marginBottom: 12,
              }}
            >
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4 }}>
                <Badge
                  tone={job.type === 'FLUX' ? 'success' : 'info'}
                  style={{ background: typeColour(job.type), color: 'white' }}
                >
                  {job.type}
                </Badge>
                <span style={{ fontSize: '0.85em', fontWeight: 500 }}>{job.description}</span>
              </div>
              <div className="job-stats" style={{ display: 'flex', gap: 12, fontSize: '0.8em', color: 'var(--text-secondary)' }}>
                <span>{job.currentStep}/{job.totalSteps} steps</span>
                <span>{job.progress}%</span>
                {job.status === 'running' && <span>ETA: {job.eta}</span>}
                <span>Cost: {job.costSoFar}</span>
              </div>
            </div>
          ))}
        </div>
      ))}

      <style>{`
        .progress-bar-container {
          width: 100%;
          height: 10px;
          background: var(--bg-elevated);
          border-radius: 5px;
          overflow: hidden;
        }
        .progress-bar-fill { height: 100%; transition: width 0.5s ease; }
        .modal-jobs-widget .account-section:last-child { margin-bottom: 0; }
      `}</style>
    </Card>
  );
}
